import { Injectable } from '@angular/core';
import { APP_QUERY_PARAMS } from '../configurations/router.config';
import { firstValueFrom } from 'rxjs';
import { ACCOUNTS_SELECTORS } from '../store/selectors/accounts.selectors';
import { APPLICATION_FILTERS_REDUCER, IApplicationFiltersState } from '../store/reducers/filters.reducer';
import { Store } from '@ngrx/store';
import { Router, UrlTree } from '@angular/router';
import { IAccount, ISite } from '../../shared/models';
import { APPLICATION_FILTERS_ACTIONS } from '../store/actions/filters.actions';
import { filter } from 'rxjs/operators';
import { SITES_SELECTORS } from '../store/selectors/sites.selectors';
import { isEmpty } from 'lodash';
import { RouterService } from './router.service';

@Injectable({
  providedIn: 'root',
})
export class QueryValidationService {
  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly routerService: RouterService,
  ) {}

  /**
   * This method realizes validation of the filters state based on available data, and return object to update it
   * if it is needed.
   */
  getValidatedState(
    availableAccounts: IAccount[],
    availableSites: ISite[],
    { accountId, siteId, accountIds, siteIds }: IApplicationFiltersState,
  ): Pick<IApplicationFiltersState, 'accountId' | 'siteId' | 'accountIds' | 'siteIds'> {
    const availableAccountIds = availableAccounts.map(({ accountId }) => accountId);

    return {
      ...this.getValidatedSingleAccountAndSiteState(availableAccountIds, availableSites, accountId, siteId),
      ...this.getValidatedMultiAccountsAndSitesState(availableAccountIds, availableSites, accountIds, siteIds),
    };
  }

  async validateAccountsAndSites(accountIds: number[], siteIds: number[]): Promise<boolean> {
    const { availableAccountIds, availableSites, filterState } = await this.getData();

    if (!this.isAccountsAndSitesValid(availableAccountIds, accountIds, availableSites, siteIds)) {
      this.routerService.navigateByAccountsAndSites(null, null);
    } else {
      this.updateFiltersState(filterState, { accountIds, siteIds });
    }

    return true;
  }

  /**
   * This method realizes validation of accountId that suppose to be passed from URL, based on available accounts list for the current user.
   * The validation process should be triggered every time when query in URL is changed to pass through the validation and update the state.
   */
  async isSingleAccountValid(path: string[], queryAccountId?: string | null): Promise<boolean | UrlTree> {
    const { availableAccountIds, filterState } = await this.getData();

    if (!queryAccountId || !availableAccountIds.includes(Number(queryAccountId))) {
      return this.router.createUrlTree(path, {
        queryParams: { [APP_QUERY_PARAMS.accountId]: filterState.accountId },
      });
    }

    this.updateFiltersState(filterState, { accountId: Number(queryAccountId) });

    return true;
  }

  /**
   * This method realizes validation of accountId and siteId that suppose to be passed from URL, based on available accounts and sites lists for the current user.
   * The validation process should be triggered every time when queries in URL are changed to pass through the validation and update the state.
   */
  // eslint-disable-next-line max-lines-per-function
  async isAccountAndSiteValid(
    path: string[],
    queryAccountId?: string,
    querySiteId?: string,
  ): Promise<UrlTree | boolean> {
    const { availableAccountIds, availableSites, filterState } = await this.getData();

    if (!queryAccountId || !availableAccountIds.includes(Number(queryAccountId))) {
      return this.router.createUrlTree(path, {
        queryParams: {
          [APP_QUERY_PARAMS.accountId]: filterState.accountId,
          [APP_QUERY_PARAMS.siteId]: filterState.siteId,
        },
      });
    }

    const accountSiteIds = availableSites
      .filter(({ accountId }) => Number(queryAccountId) === accountId)
      .map(({ id }) => id);

    if (!accountSiteIds.length && querySiteId) {
      return this.router.createUrlTree(path, {
        queryParams: { [APP_QUERY_PARAMS.accountId]: Number(queryAccountId), [APP_QUERY_PARAMS.siteId]: null },
      });
    }

    if (accountSiteIds.length && !accountSiteIds.includes(Number(querySiteId))) {
      return this.router.createUrlTree(path, {
        queryParams: {
          [APP_QUERY_PARAMS.accountId]: Number(queryAccountId),
          [APP_QUERY_PARAMS.siteId]: accountSiteIds[0],
        },
      });
    }

    this.updateFiltersState(filterState, { accountId: Number(queryAccountId), siteId: Number(querySiteId) });

    return true;
  }

  private async getData(): Promise<{
    availableAccountIds: number[];
    availableSites: ISite[];
    filterState: IApplicationFiltersState;
  }> {
    const availableAccountIds$ = firstValueFrom(
      this.store.select(ACCOUNTS_SELECTORS.selectAccountIds).pipe(filter((data) => Boolean(data.length))),
    ) as Promise<number[]>;
    const availableSites$ = firstValueFrom(
      this.store.select(SITES_SELECTORS.selectAllSites).pipe(filter((data) => Boolean(data.length))),
    );
    const filterState$ = firstValueFrom(
      this.store
        .select(APPLICATION_FILTERS_REDUCER['selectApp-filtersState'])
        .pipe(filter(({ validated }) => validated)),
    );

    const [availableAccountIds, availableSites, filterState] = await Promise.all([
      availableAccountIds$,
      availableSites$,
      filterState$,
    ]);

    return { availableAccountIds, availableSites, filterState };
  }

  private isAccountsAndSitesValid(
    availableAccountIds: number[],
    accountIdsToValidate: number[],
    availableSites: ISite[],
    siteIdsToValidate: number[],
  ): boolean {
    const validatedAccountIds = accountIdsToValidate.filter((id) => availableAccountIds.includes(id));

    if (validatedAccountIds.length !== accountIdsToValidate.length) {
      return false;
    }
    const availableAccountsSiteIds = availableSites
      .filter(({ accountId }) => accountIdsToValidate.includes(accountId))
      .map(({ id }) => id);
    const validatedSiteIds = siteIdsToValidate.filter((id) => availableAccountsSiteIds.includes(id));

    return validatedSiteIds.length === siteIdsToValidate.length;
  }

  private updateFiltersState(
    state: IApplicationFiltersState,
    value: Partial<Pick<IApplicationFiltersState, 'accountId' | 'accountIds' | 'siteId' | 'siteIds'>>,
  ): void {
    const updatePayload = {} as Partial<IApplicationFiltersState>;

    if (
      Array.isArray(value['accountIds']) &&
      [...state.accountIds].sort().toString() !== [...value.accountIds].sort().toString()
    ) {
      updatePayload.accountIds = value.accountIds;
      updatePayload.siteIds = Array.isArray(value['siteIds']) ? value['siteIds'] : [];
    }
    if (
      Array.isArray(value['siteIds']) &&
      [...state.siteIds].sort().toString() !== [...value.siteIds].sort().toString()
    ) {
      updatePayload.siteIds = value.siteIds;
    }
    if (value['accountId'] && state.accountId !== value['accountId']) {
      updatePayload.accountId = value['accountId'];
      updatePayload.siteId = null;
    }
    if (value['siteId'] && value['siteId'] !== state.siteId) updatePayload.siteId = value['siteId'];

    if (!isEmpty(updatePayload)) {
      this.store.dispatch(APPLICATION_FILTERS_ACTIONS.update({ data: updatePayload }));
    }
  }

  private getValidatedSingleAccountAndSiteState(
    availableAccountIds: number[],
    availableSites: ISite[],
    accountId: number | null,
    siteId: number | null,
  ): Pick<IApplicationFiltersState, 'accountId' | 'siteId'> {
    const stateToUpdate: Pick<IApplicationFiltersState, 'accountId' | 'siteId'> = {} as never;

    stateToUpdate['accountId'] =
      !accountId || !availableAccountIds.includes(accountId) ? availableAccountIds[0] : accountId;

    const availableSiteIdsByAccount = availableSites
      .filter(({ accountId }) => stateToUpdate['accountId'] === accountId)
      .map(({ id }) => id);

    if (availableSiteIdsByAccount.length) {
      stateToUpdate['siteId'] = !availableSiteIdsByAccount.includes(siteId as never)
        ? availableSiteIdsByAccount[0]
        : siteId;
    } else {
      stateToUpdate['siteId'] = null;
    }

    return stateToUpdate;
  }

  private getValidatedMultiAccountsAndSitesState(
    availableAccountIds: number[],
    availableSites: ISite[],
    accountIds: number[],
    siteIds: number[],
  ): Pick<IApplicationFiltersState, 'accountIds' | 'siteIds'> {
    const stateToUpdate: Pick<IApplicationFiltersState, 'accountIds' | 'siteIds'> = {} as never;

    if (accountIds.length) {
      const validatedAccountIds = accountIds.filter((id) => availableAccountIds.includes(id));
      stateToUpdate['accountIds'] = validatedAccountIds.length !== accountIds.length ? availableAccountIds : accountIds;
    } else {
      stateToUpdate['accountIds'] = availableAccountIds;
    }

    const availableSiteIdsByAccounts = availableSites
      .filter(({ accountId }) => stateToUpdate['accountIds'].includes(accountId))
      .map(({ id }) => id);

    if (siteIds.length) {
      const validatedSiteIds = siteIds.filter((id) => availableSiteIdsByAccounts.includes(id));
      stateToUpdate['siteIds'] = validatedSiteIds.length !== siteIds.length ? availableSiteIdsByAccounts : siteIds;
    } else {
      stateToUpdate['siteIds'] = availableSiteIdsByAccounts;
    }

    return stateToUpdate;
  }
}
